<script lang="ts">
  import { getPageCategory } from '../pageCategory'
  import { getI18n } from '../i18n'
  import type { TableOfContentsEntry } from '../types'

  // Each entry's `id` is the DOM id of the heading it links to.
  let { entries }: { entries: TableOfContentsEntry[] } = $props()

  const pageCategory = getPageCategory()
  const i18n = getI18n()

  const SUBPIXEL_TOLERANCE = 1
  // Keeps the active entry clear of the fade, whose height is --space-12.
  const FADE_HEIGHT = 48

  let activeId = $state<string | null>(null)
  let navigationElement = $state<HTMLElement>()
  let hasSectionsAbove = $state(false)
  let hasSectionsBelow = $state(false)

  $effect(() => {
    activeId = entries[0]?.id ?? null

    const elements = entries
      .map((entry) => document.getElementById(entry.id))
      .filter((element): element is HTMLElement => element !== null)

    const visibleIds = new Set<string>()

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            visibleIds.add(entry.target.id)
          } else {
            visibleIds.delete(entry.target.id)
          }
        })

        const lastVisible = elements.reduce<HTMLElement | null>(
          (result, element) => (visibleIds.has(element.id) ? element : result),
          null,
        )
        if (lastVisible) {
          activeId = lastVisible.id
        }
      },
      { rootMargin: '0px 0px -50% 0px' },
    )

    elements.forEach((element) => observer.observe(element))

    return () => observer.disconnect()
  })

  $effect(() => {
    if (!navigationElement) return

    const activeLink = activeId ? navigationElement.querySelector<HTMLElement>(`[href="#${CSS.escape(activeId)}"]`) : null

    if (!activeLink) return

    const navigationBounds = navigationElement.getBoundingClientRect()
    const linkBounds = activeLink.getBoundingClientRect()
    const readableTop = navigationBounds.top + FADE_HEIGHT
    const readableBottom = navigationBounds.bottom - FADE_HEIGHT

    if (linkBounds.top < readableTop) {
      navigationElement.scrollTop -= readableTop - linkBounds.top
    } else if (linkBounds.bottom > readableBottom) {
      navigationElement.scrollTop += linkBounds.bottom - readableBottom
    }
  })

  function updateClipState() {
    if (!navigationElement) return

    hasSectionsAbove = navigationElement.scrollTop > SUBPIXEL_TOLERANCE
    hasSectionsBelow = navigationElement.scrollTop + navigationElement.clientHeight < navigationElement.scrollHeight - SUBPIXEL_TOLERANCE
  }

  $effect(updateClipState)
</script>

<nav
  class={['table-of-contents', {
    '-sections-above': hasSectionsAbove,
    '-sections-below': hasSectionsBelow,
    '-variant-brand': pageCategory.variant === 'brand',
    '-variant-warning': pageCategory.variant === 'warning',
    '-variant-success': pageCategory.variant === 'success',
  }]}
  aria-label={i18n.t('page.tableOfContents')}
  bind:this={navigationElement}
  onscroll={updateClipState}
>
  <span class="table-of-contents-label">{i18n.t('page.tableOfContents')}</span>
  <ul class="table-of-contents-list">
    {#each entries as entry (entry.id)}
      <li class="table-of-contents-item">
        <a
          class={['table-of-contents-link', { '-level-4': entry.level === 4, '-level-5': entry.level === 5, '-active': entry.id === activeId }]}
          aria-current={entry.id === activeId ? 'location' : undefined}
          href="#{entry.id}"
        >
          {entry.title}
        </a>
      </li>
    {/each}
  </ul>
</nav>

<style>
  .table-of-contents {
    --table-of-contents-fade-top: 0px;
    --table-of-contents-fade-bottom: 0px;

    display: none;
  }

  @media (--desktop) {
    .table-of-contents:has(.table-of-contents-item:nth-of-type(4)) {
      display: block;
      position: sticky;
      top: 0;
      height: 100vh;
      box-sizing: border-box;
      padding-block: var(--space-9);
      overflow: hidden;

      @media (prefers-reduced-motion: no-preference) {
        scroll-behavior: smooth;
      }

      mask-image: linear-gradient(
        to bottom,
        transparent 0,
        black var(--table-of-contents-fade-top),
        black calc(100% - var(--table-of-contents-fade-bottom)),
        transparent 100%
      );
    }
  }

  .table-of-contents.-sections-above {
    --table-of-contents-fade-top: var(--space-12);
  }

  .table-of-contents.-sections-below {
    --table-of-contents-fade-bottom: var(--space-12);
  }

  .table-of-contents-label {
    display: block;
    font-size: var(--font-size-2);
    font-weight: var(--font-weight-3);
    text-transform: uppercase;
    letter-spacing: var(--letter-spacing-2);
    color: var(--color-text-muted);
    margin-bottom: var(--space-5);
  }

  .table-of-contents-list {
    list-style: none;
    margin: 0;
    padding: 0;
    border-inline-start: var(--border-2) solid var(--color-border-2);
    display: flex;
    flex-direction: column;
  }

  .table-of-contents-link {
    display: block;
    padding-block: var(--space-3);
    padding-inline: var(--space-6) 0;
    margin-inline-start: calc(var(--border-2) * -1);
    border-inline-start: var(--border-2) solid transparent;
    font-size: var(--font-size-4);
    line-height: var(--line-height-3);
    color: var(--color-text-muted);
    text-decoration: none;
    border-start-end-radius: var(--radius-1);
    border-end-end-radius: var(--radius-1);
    transition:
      color var(--transition-medium),
      background-color var(--transition-medium),
      border-color var(--transition-medium);
  }

  .table-of-contents-link:hover {
    color: var(--color-text);
  }

  .table-of-contents.-variant-brand .table-of-contents-link.-active {
    color: var(--color-variant-brand);
    background-color: var(--color-variant-brand-muted);
    border-inline-start-color: var(--color-variant-brand);
  }

  .table-of-contents.-variant-warning .table-of-contents-link.-active {
    color: var(--color-variant-warning);
    background-color: var(--color-variant-warning-muted);
    border-inline-start-color: var(--color-variant-warning);
  }

  .table-of-contents.-variant-success .table-of-contents-link.-active {
    color: var(--color-variant-success);
    background-color: var(--color-variant-success-muted);
    border-inline-start-color: var(--color-variant-success);
  }

  .table-of-contents-link.-level-4 {
    padding-inline-start: calc(var(--space-6) * 2);
    font-size: var(--font-size-3);
  }

  .table-of-contents-link.-level-5 {
    padding-inline-start: calc(var(--space-6) * 3);
    font-size: var(--font-size-2);
    opacity: var(--opacity-4);
  }
</style>
